import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import companiesLogos from '../../../assets/js/companiesLogos';

const fmt = (amount) => `₪${Number(amount).toLocaleString('he-IL')}`;

// The accessibility menu switches the theme to dark mode for high contrast.
const isHighContrast = (theme) => theme.palette.mode === 'dark';

const styles = {
  card: (theme) => ({
    mb: 2,
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: '24px',
    boxShadow: `0 16px 40px ${alpha(theme.palette.common.black, 0.08)}`,
    overflow: 'hidden',
  }),
  savingsBanner: (theme) => ({
    background: isHighContrast(theme)
      ? theme.palette.success.light
      : `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
    px: 2.5,
    py: 2,
    color: isHighContrast(theme) ? 'success.main' : 'common.white',
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  }),
  savingsArrow: (theme) => ({
    width: 40,
    height: 40,
    borderRadius: '50%',
    bgcolor: alpha(theme.palette.common.white, 0.18),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  savingsLabel: (theme) => ({
    fontSize: theme.typography.pxToRem(13),
    opacity: 0.85,
    mb: 0.25,
  }),
  savingsRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 1,
    flexWrap: 'wrap',
  },
  savingsAmount: (theme) => ({
    fontSize: theme.typography.pxToRem(28),
    fontWeight: 800,
  }),
  savingsCompare: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    opacity: 0.85,
  }),
  priceBody: {
    px: 2.75,
    py: 2.5,
  },
  pricingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    mb: 2,
  },
  specialPrice: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    color: 'text.secondary',
  }),
  insuranceCompany: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: 'success.main',
  }),
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 1,
    mb: 0.5,
    flexWrap: 'wrap',
  },
  totalPremium: (theme) => ({
    fontSize: theme.typography.pxToRem(40),
    fontWeight: 800,
    color: 'secondary.main',
    lineHeight: 1,
  }),
  perYear: (theme) => ({
    fontSize: theme.typography.pxToRem(15),
    color: 'text.secondary',
  }),
  lastYearRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 2,
    flexWrap: 'wrap',
  },
  lastYearLabel: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    color: 'text.secondary',
  }),
  lastYearAmount: (theme) => ({
    fontSize: theme.typography.pxToRem(16),
    color: 'error.main',
    textDecoration: 'line-through',
  }),
  savingsBadge: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    border: '1.5px solid',
    borderColor: 'success.main',
    color: 'success.main',
    borderRadius: '20px',
    px: 2,
    py: 0.75,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    mb: 2,
    width: 'fit-content',
  }),
  breakdown: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: 'text.secondary',
    flexWrap: 'wrap',
  }),
};

const RenewalOfferCard = ({ offerData, policy = {}, labels }) => {
  const hasSavings = offerData.savings > 0;
  const companyLogo = policy.insuranceCompanyLogo
    ? companiesLogos.find((elm) => elm.key === policy.insuranceCompanyLogo)
    : null;

  return (
    <Box sx={styles.card}>
      {hasSavings && (
        <Box sx={styles.savingsBanner}>
          <Box sx={styles.savingsArrow}>
            <ArrowDownwardIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={styles.savingsLabel}>
              {labels.savingsLabel}
            </Typography>
            <Typography component='div' sx={styles.savingsRow}>
              <Box component='span' sx={styles.savingsAmount}>
                {fmt(offerData.savings)} {labels.less}
              </Box>
              <Box component='span' sx={styles.savingsCompare}>
                {labels.savingsCompare}
              </Box>
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={styles.priceBody}>
        <Box sx={styles.pricingHeader}>
          <Typography sx={styles.specialPrice}>
            {labels.specialPrice}
          </Typography>
          {companyLogo ? (
            <Box component='img' src={companyLogo.imgSrc_small} alt={policy.insuranceCompany} sx={{ height: 32, width: 'auto' }} />
          ) : (
            <Typography sx={styles.insuranceCompany}>
              {policy.insuranceCompany}
            </Typography>
          )}
        </Box>

        <Box sx={styles.priceRow}>
          <Typography sx={styles.totalPremium}>
            {fmt(offerData.totalPremium)}
          </Typography>
          <Typography sx={styles.perYear}>
            / {labels.perYear}
          </Typography>
        </Box>

        {offerData.lastYearTotalPremium > 0 && (
          <Box sx={styles.lastYearRow}>
            <Typography sx={styles.lastYearLabel}>
              {labels.lastYearPrice}
            </Typography>
            <Typography sx={styles.lastYearAmount}>
              {fmt(offerData.lastYearTotalPremium)}
            </Typography>
          </Box>
        )}

        {hasSavings && (
          <Box sx={styles.savingsBadge}>
            ↓ {labels.savingsBadge}
          </Box>
        )}

        <Box sx={styles.breakdown}>
          <span>{labels.breakdownMandatory}</span>
          <span>+</span>
          <span>{labels.breakdownCasco}</span>
          {offerData.serviceFee > 0 && (
            <>
              <span>+</span>
              <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {labels.breakdownServiceFee}
                <Tooltip
                  title={labels.serviceFeeDisclaimer}
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={4000}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 15, color: 'secondary.main', cursor: 'pointer' }} />
                </Tooltip>
              </Box>
            </>
          )}
          {labels.breakdownOtherProducts?.map((text) => (
            <React.Fragment key={text}>
              <span>+</span>
              <span>{text}</span>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

RenewalOfferCard.propTypes = {
  labels: PropTypes.shape({
    breakdownCasco: PropTypes.string.isRequired,
    breakdownMandatory: PropTypes.string.isRequired,
    breakdownOtherProducts: PropTypes.arrayOf(PropTypes.string),
    breakdownServiceFee: PropTypes.string,
    serviceFeeDisclaimer: PropTypes.string,
    lastYearPrice: PropTypes.string.isRequired,
    less: PropTypes.string.isRequired,
    perYear: PropTypes.string.isRequired,
    savingsBadge: PropTypes.string.isRequired,
    savingsCompare: PropTypes.string.isRequired,
    savingsLabel: PropTypes.string.isRequired,
    specialPrice: PropTypes.string.isRequired,
  }).isRequired,
  offerData: PropTypes.shape({
    cascoPremium: PropTypes.number,
    lastYearTotalPremium: PropTypes.number,
    mandatoryPremium: PropTypes.number,
    savings: PropTypes.number,
    serviceFee: PropTypes.number,
    totalPremium: PropTypes.number,
  }).isRequired,
  policy: PropTypes.shape({
    insuranceCompany: PropTypes.string,
    insuranceCompanyLogo: PropTypes.string,
  }),
};

export default RenewalOfferCard;
